<template lang="pug">
.relative(v-click-outside-element="clickOutside")
  div(
    v-if="label"
    :class="[labelClasses, $style.label, 'label-position-' + labelPosition]")
    label(:class="$style.text") {{ label }}

  div(
    :class="[classes.join(' '), customClasses, {[$style.active]: isOpen, [$style.error]: error, [$style.mainTextDisabled]: disabled, [$style.notInForm]: notInForm}]"
    @click="setOpen(!isOpen)"
    :style="styles.join(';')")
    //- Selected text
    div(:class="{[$style.selectedOption]: true, [$style.placeholder]: placeholder === getSelectedText}") {{ getSelectedTextTruncated }}
    //- Options list
    ul(
      v-show="isOpen"
      :class="[$style.options, optionIcon ? $style.optionsWithIcon : '', subMenu ? $style.subMenu : '']"
      :style="optionsHeight")
      li.select-option(
        v-if="subMenu"
        @click.stop="selectOption({value: ''})"
      )
        Icon(
          name="i:angle-left-solid"
          class="text-black70"
          :size="16")
        span.ml-ds-sm
          | {{backButtonTitle}}
      li.select-option(
        v-for="(option) of selectableOptions"
        :key="parsedValue(option)"
        :class="{'is-selected bg-blue-primary text-white': isOptionSelected(option)}"
        @click.stop="selectOption(option)"
        v-html="parsedText(option)" )
    template(v-if="indicator")
      Icon(
        v-if="isOpen"
        :class="[$style.angle, 'text-black70']"
        :color="iconColor"
        name="i:chevron-up-1"
        :size="(size === 'xs') ? 10 : 14")
      Icon(
        v-else
        :class="[$style.angle, 'text-black70']"
        :color="iconColor"
        name="i:chevron-down"
        :size="(size === 'xs') ? 10 : 14")

    li(
      v-if="iconLeft"
      class="left-0 ml-ds-xxxs"
      :class="$style.placeholderIcon")
      Icon(
        :name="iconLeft"
        :size="iconFontSize"
        :style="iconStyles.join(';')")

  //- Error message
  div(
    v-if="error"
    :class="$style.msg")
    Icon(
      class="mt-ds-xxxs flex-none"
      name="i:triangle-exclamation-solid"
      :size="16")
    div(:class="$style.msgTxt") {{ error }}
</template>

<script lang="ts">
import diff from "deep-diff"
import { getSizeClasses, getSelectSizeClasses } from "~/scripts/css-helper"
import { defineComponent } from "vue"

/**
 * Create a select like input (not real `select` element).
 * ```
 * let options = [
 *   {
 *     value: 1,
 *     name: "First option"
 *   },
 *   {
 *     value: 2,
 *     name: "Second option"
 *   },
 *   {
 *     value: 3,
 *     name: "Third option"
 *   }
 * ]
 * ```
 * `Sel(:options="options" option-value="value" option-text="name") `
 */

export default defineComponent({
  name: "SelRefined",

  props: {
    label: {
      type: String,
      default: ""
    },
    labelPosition: {
      type: String,
      default: "top",
      validator: value => ["top", "left", "animated", "border"].includes(value)
    },
    /** Options list */
    options: {
      type: Array,
      required: true
    },
    /**
     * Property name from an `Option` object. Set only if you want to use the property
     * value as `Sel` key, otherwise the whole object will be the key (and `v-model`’s value).
     */
    // eslint-disable-next-line vue/require-default-prop
    optionValue: {
      type: String,
      required: false
    },
    /**
     * Property name from an `Option` object. Set only if you want to display this property in the
     * `Sel` dropdown, otherwise the whole object will be printed.
     */
    // eslint-disable-next-line vue/require-default-prop
    optionText: {
      type: String,
      required: false
    },
    /** Selected option */
    // eslint-disable-next-line
    value: {
      // type: [Object, Boolean, String, Number, null],
      default: null
    },
    /** Default text if nothing selected */
    placeholder: {
      type: String,
      default: "Please Select"
    },
    optionsToShow: {
      type: Number,
      default: 0
    },
    size: {
      type: String,
      default: "xl",
      validator: value => ["xs", "s", "m", "l", "xl"].includes(value)
    },
    /** Error message */
    error: {
      type: String,
      default: ""
    },
    /** The selected text will be the placeholder - need in postcode search component */
    alwaysPlaceholder: {
      type: Boolean,
      default: false
    },
    /** We can remove the selected option from the dropdown */
    removeSelected: {
      type: Boolean,
      default: false
    },
    truncateLength: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    /** Icon shortcut */
    iconLeft: {
      type: String,
      default: ""
    },
    /** Icon shortcut */
    iconRight: {
      type: String,
      default: ""
    },
    /** Icon size */
    // eslint-disable-next-line vue/require-default-prop
    iconSize: {
      type: Number,
      required: false
    },
    /** Icon colour */
    // eslint-disable-next-line vue/require-default-prop
    iconColor: {
      type: String,
      required: false
    },
    border: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: ""
    },
    iconPadding: {
      type: Number,
      required: false,
      default: null
    },
    iconPaddingEnabled: {
      type: Boolean,
      default: false
    },
    indicator: {
      type: Boolean,
      default: false
    },
    initialOpen: {
      type: Boolean,
      default: false
    },
    subMenu: {
      type: Boolean,
      default: false
    },
    optionIcon: {
      type: Array,
      default: null
    },
    notInForm: {
      type: Boolean,
      default: false
    },
    customClasses: {
      type: String
    }
  },
  emits: ['input', 'update:modelValue', 'select'],

  data() {
    return {
      isOpen: this.initialOpen,
      xPadding: null
    }
  },

  computed: {
    labelClasses() {
      return getSizeClasses(this.size)
    },

    classes() {
      return [this.$style.sel, ...getSelectSizeClasses(this.size)]
    },

    // :style="styles() ? styles().join(';') : ''")
    // Unselected options for the dropdown
    selectableOptions() {
      if (this.alwaysPlaceholder || !this.removeSelected) {
        return this.options
      }

      return this.optionValue
        ? this.options.filter(option => option[this.optionValue] !== this.value)
        : this.options.filter(option => JSON.stringify(option) !== JSON.stringify(this.value))
    },

    getSelectedText() {
      if (this.value === null || this.alwaysPlaceholder) {
        return this.placeholder
      }

      const value = this.optionValue ? this.value : this.value.value
      const valueField = this.optionValue ? this.optionValue : "value"

      if (typeof this.options.find(option => option[valueField] === value) === "undefined") {
        return this.placeholder
      }

      return this.optionText
        ? this.options.find(option => option[valueField] === value)[this.optionText]
        : this.options.find(option => option[valueField] === value).name
    },

    getSelectedTextTruncated() {
      return this.truncate(this.getSelectedText, this.truncateLength, "...")
    },

    iconFontSize() {
      let size = 32

      if (this.iconSize) {
        size = this.iconSize
      } else {
        switch (this.size) {
          case "xs":
            size = 16
            break
          case "s":
            size = 18
            break
          case "m":
            size = 20
            break
          case "l":
            size = 24
            break
          case "xl":
            size = 32
            break
        }
      }

      return size
    },

    iconStyles() {
      const styles = []

      if (this.iconPaddingEnabled) {
        if (this.iconLeft || this.iconRight) {
          styles.push(`width: ${this.xPadding}px`)
        }
      }

      if (this.iconColor) {
        styles.push(`color: ${this.iconColor}`)
      }

      return styles
    },

    styles() {
      const styles = []

      if (!this.indicator) {
        styles.push(`padding-right: 0`)
      }

      if (this.iconPaddingEnabled) {
        if (this.iconLeft) {
          styles.push(`padding-left: ${this.xPadding}px`)
        }

        if (this.iconRight) {
          styles.push(`padding-right: ${this.xPadding}px`)
        }
      }

      if (!this.border) {
        styles.push(`border: 0`)
      }

      if (this.backgroundColor) {
        styles.push(`background-color: ${this.backgroundColor}`)
      }

      return styles
    },

    optionsHeight() {
      let maxHeight: null | number = null
      if (this.optionsToShow) {
        switch (this.size) {
          case "xs":
            maxHeight = this.optionsToShow * 30 + 5
            break
          case "s":
            maxHeight = this.optionsToShow * 41 + 5
            break
          case "m":
            maxHeight = this.optionsToShow * 47 + 5
            break
          case "l":
            maxHeight = this.optionsToShow * 50 + 5
            break
        }
      }

      return maxHeight ? "max-height: " + maxHeight + "px;" : ''
    },

    selectedOption: {
      get() {
        return this.value ? (this.optionValue ? this.value : JSON.stringify(this.value)) : null
      },
      set(newValue) {
        this.selectOption(newValue)
      }
    },

    backButtonTitle() {
      return this.selectedOption.search(/flat/i) > -1
        ? this.$t("location_form.flat")
        : this.selectedOption.search(/house/i) > -1
          ? this.$t("location_form.house")
          : this.selectedOption
    }
  },

  mounted() {
    if (this.iconLeft || this.iconRight) {
      if (this.iconPadding) {
        this.xPadding = this.iconPadding
      } else {
        this.xPadding = this.iconSize ? this.iconSize * 2 : this.iconFontSize * 2
      }
    }
  },

  methods: {
    // Option id
    parsedValue(option) {
      return this.optionValue ? option[this.optionValue] : JSON.stringify(option)
    },
    // Option text
    parsedText(option) {
      return this.optionText ? option[this.optionText] : option
    },

    setOpen(isOpen: boolean) {
      if (isOpen && this.$props.disabled) {
        return
      }

      if (isOpen && (!this.selectableOptions || !this.selectableOptions.length)) {
        return
      }

      this.isOpen = isOpen
    },

    selectOption(option) {
      this.$emit("input", this.optionValue ? option[this.optionValue] : option.value)
      this.$emit("update:modelValue", this.optionValue ? option[this.optionValue] : option.value)
      this.$emit("select", option)
      this.setOpen(!this.isOpen)
    },

    clickOutside() {
      this.isOpen = false
    },

    isOptionSelected(option) {
      return option.value === this.selectedOption
    },

    truncate(text, length, suffix) {
      if (length === "") {
        return text
      }

      length = parseInt(length)
      if (length && text.length > length) {
        return text.substring(0, length) + suffix
      }

      return text
    }
  }
})
</script>

<style lang="scss" module>
$padding: 15px;

.sel {
  @apply flex relative text-gray-800 bg-transparent items-center cursor-pointer p-ds-md border border-mono-200 rounded-md;
}
.sel.notInForm {
  @apply border-0;
}

.selectedOption {
  @apply w-full truncate;
}

.label {
  @apply table;
  padding-right: 1em;

  .text {
    @apply table-cell align-middle;
  }

  &:global(.label-position-border) {
    @apply absolute text-gray-550 bg-white;
    z-index: 10;
    left: 26px;
    padding: 0 4px;
    top: -10px;
    font-size: 0.875rem;
    height: 20px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0) 40%,
      rgba(255, 255, 255, 1) 45%,
      rgba(255, 255, 255, 1) 100%
    );
  }
}

.placeholder {
  @apply text-gray-600;
}

:global {
  :local(.sel) {
    :local(.angle) {
      @apply absolute pointer-events-none;
      right: 8px;
    }

    &.size-extra-small {
      min-height: 32px;
      line-height: 18px;
      padding-left: 8px;
      padding-right: 28px;

      :local(.angle) {
        right: 2px;
      }

      :local(.carets) {
        right: 4px;
      }

      :local(.options) {
        & > li {
          padding: 6px 8px;
        }
      }
    }

    &.size-small {
      height: 54px;
      min-height: 40px;
      line-height: 28px;
    }

    &.size-medium {
      height: 48px;
      min-height: 48px;
      line-height: 34px;

      :local(.carets) {
        right: 10px;
      }
    }

    &.size-large {
      height: 56px;
      min-height: 56px;
      line-height: 44px;

      :local(.carets) {
        right: 12px;
      }
    }

    &.size-xl {
      height: 60px;
      min-height: 60px;
      line-height: 44px;

      :local(.carets) {
        right: 12px;
      }
    }
  }
}

.active {
  @apply border-blue-primary rounded-b-none cursor-default;
}

.options {
  @apply absolute bg-white border border-solid border-blue-primary overflow-y-auto;
  z-index: 100;
  border-top: 0;
  border-radius: 0 0 6px 6px;
  top: 100%;
  left: -1px;
  right: -1px;

  > li {
    @apply cursor-pointer text-lg px-ds-md py-ds-md;
    &:not(:global(.is-selected)):hover {
      @apply bg-blue-soft;
    }
    &:not(:global(.is-selected)):active {
      @apply bg-blue-primary text-white;
    }
    &:last-child {
      border-radius: 0 0 6px 6px;
    }
  }
}

.sel.notInForm .options {
  @apply rounded-md border border-solid border-blue-primary top-ds-ml;
}

.optionsWithIcon {
  border: 1px solid #d8d8d8;
  border-radius: 6px;
  box-shadow:
    0px 2px 6px 2px rgba(0, 0, 0, 0.15),
    0px 1px 2px 0px rgba(0, 0, 0, 0.3);
}

.error {
  @apply border-red-primary;
  border-bottom-color: transparent;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;

  .options {
    @apply border-red-primary;
  }
}

.msg {
  @apply font-latoMedium text-red-primary flex bg-gray-110 border border-solid border-red-primary;
  padding: calc($padding / 2) $padding;
  border-bottom-left-radius: var(--border-radius);
  border-bottom-right-radius: var(--border-radius);

  .msgTxt {
    @apply relative inline-block font-bold;
    font-size: 15px;
    top: -1px;
    margin-left: 5px;
  }
}

.mainTextDisabled {
  @apply text-gray-320 bg-gray-100 cursor-not-allowed;
}

.placeholderIcon {
  @apply absolute h-full top-0 flex items-center justify-center;
  width: 50px;
}
</style>
